/**
 * Menu - Shared menu state and handlers.
 * State lives in menuStore (Zustand); this hook composes store + auth/dialog/router for handlers.
 */

import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useDialog } from '../../shared/hooks/useDialog';
import { useMenuStore } from '../../shared/stores/menuStore';
import { buildPublicSignInHref } from '../../signIn/helpers/postAuthRedirect.helper';
import { PUBLIC_WELCOME_PATH } from '../../signIn/constants/publicAuthRoutes.constants';

export interface MenuContextValue {
  menuVisible: boolean;
  setMenuVisible: (visible: boolean) => void;
  handleMenuPress: () => void;
  handleSignOut: () => Promise<void>;
  tutorialFromMenuVisible: boolean;
  setTutorialFromMenuVisible: (visible: boolean) => void;
  openTutorialFromMenu: () => void;
  layeredAvatarLabVisible: boolean;
  reopenMenuOnCustomizerClose: boolean;
  closeLayeredAvatarLab: () => void;
  clearReopenMenuOnCustomizerClose: () => void;
  openLayeredAvatarLabFromMenu: () => void;
}

export const useMenuContext = (): MenuContextValue => {
  const menuVisible = useMenuStore((s) => s.menuVisible);
  const setMenuVisible = useMenuStore((s) => s.setMenuVisible);
  const tutorialFromMenuVisible = useMenuStore((s) => s.tutorialFromMenuVisible);
  const setTutorialFromMenuVisible = useMenuStore((s) => s.setTutorialFromMenuVisible);
  const layeredAvatarLabVisible = useMenuStore((s) => s.layeredAvatarLabVisible);
  const reopenMenuOnCustomizerClose = useMenuStore((s) => s.reopenMenuOnCustomizerClose);
  const openLayeredAvatarLab = useMenuStore((s) => s.openLayeredAvatarLab);
  const closeLayeredAvatarLab = useMenuStore((s) => s.closeLayeredAvatarLab);
  const clearReopenMenuOnCustomizerClose = useMenuStore((s) => s.clearReopenMenuOnCustomizerClose);

  const { signOut, isAnonymous } = useSupabaseAuth();
  const router = useRouter();
  const { showDialog, showAlert } = useDialog();
  const { track } = useAnalytics();

  const handleMenuPress = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_OPENED);
    setMenuVisible(true);
  }, [track, setMenuVisible]);

  const openTutorialFromMenu = useCallback(() => {
    setMenuVisible(false);
    setTutorialFromMenuVisible(true);
  }, [setMenuVisible, setTutorialFromMenuVisible]);

  const openLayeredAvatarLabFromMenu = useCallback(() => {
    setMenuVisible(false);
    // Drilled in from the drawer, so closing the customizer should land back on the menu.
    openLayeredAvatarLab({ reopenMenuOnClose: true });
  }, [setMenuVisible, openLayeredAvatarLab]);

  const handleSignOut = useCallback(async () => {
    // Warn anonymous users: Sign Up keeps the session (same user id); only explicit Sign Out deletes guest data.
    if (isAnonymous) {
      const choice = await showDialog({
        title: 'Your progress will be lost',
        message:
          'You haven\'t signed up yet. Signing out will permanently delete your game history. Sign up first to keep it.',
        showCancelButton: false,
        destructiveText: 'Sign Out',
        confirmText: 'Sign Up',
      });
      if (choice === false) {
        return;
      }
      setMenuVisible(false);
      if (choice === 'destructive') {
        try {
          await signOut();
          // Guest signed out (progress discarded) → guest-first Welcome, not the sign-in form.
          router.replace(PUBLIC_WELCOME_PATH);
        } catch {
          await showAlert({
            title: 'Error',
            message: 'Failed to sign out. Please try again.',
            type: 'danger',
          });
        }
        return;
      }
      // "Sign Up" path: navigate to sign-up without deleting the anonymous account.
      // The sign-in page's upgrade flow (supabase.auth.updateUser) preserves the user id.
      router.replace(buildPublicSignInHref({ signup: true }) as `/${string}`);
      return;
    } else {
      const confirmed = await showDialog({
        title: 'Sign Out',
        message: 'Are you sure you want to sign out?',
        type: 'danger',
        confirmText: 'Sign Out',
        cancelText: 'Cancel',
      });
      if (!confirmed) return;
    }
    try {
      await signOut();
      // Land on the guest-first Welcome front door, not the sign-in form (guest-first AC-4b).
      router.replace(PUBLIC_WELCOME_PATH);
    } catch {
      await showAlert({
        title: 'Error',
        message: 'Failed to sign out. Please try again.',
        type: 'danger',
      });
    }
  }, [signOut, isAnonymous, router, showDialog, showAlert, setMenuVisible]);

  return {
    menuVisible,
    setMenuVisible,
    handleMenuPress,
    handleSignOut,
    tutorialFromMenuVisible,
    setTutorialFromMenuVisible,
    openTutorialFromMenu,
    layeredAvatarLabVisible,
    reopenMenuOnCustomizerClose,
    closeLayeredAvatarLab,
    clearReopenMenuOnCustomizerClose,
    openLayeredAvatarLabFromMenu,
  };
};
